import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Info as InfoIcon, Trash2, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { createOrder, fetchSettings, getAllTerms } from "@/lib/api";
import AddressFields, { isAddressComplete } from "@/components/AddressFields";
import { fmtCAD } from "@/lib/currency";
import { renderMarkdown } from "@/lib/markdown";
import { testIds } from "@/constants/testIds/b2b";

const FULFILLMENT_LABEL = {
  prebook: "Prebook",
  in_stock: "In-Stock",
};

export default function Checkout() {
  const { items, subtotal, totalUnits, moqViolations, minOrderUnits, belowMinOrder, unitsToMin, clearCart, updateQty, removeItem } = useCart();
  const { user, isApproved, loading: authLoading } = useAuth();
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [placedOrders, setPlacedOrders] = useState([]);
  const [err, setErr] = useState("");
  const [termsByKind, setTermsByKind] = useState(null); // { prebook, in_stock }
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([getAllTerms(), fetchSettings()]);
        setTermsByKind(t);
        setSettings(s);
      } catch (e) {
        // non-blocking
      }
    })();
  }, []);

  if (authLoading) {
    return <div className="min-h-[60vh] flex items-center justify-center text-foreground/70" data-testid="checkout-loading">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" state={{ from: "/checkout" }} replace />;
  if (!isApproved) return <Navigate to="/cart" replace />;
  if (items.length === 0 && !submitted) return <Navigate to="/cart" replace />;

  // Figure out which terms kinds apply to this cart based on each line's fulfillment.
  const cartKinds = (() => {
    const kinds = new Set();
    for (const it of items) {
      const ft = it.fulfillment_type === "in_stock" ? "in_stock" : "prebook";
      kinds.add(ft);
    }
    // Fallback: if cart somehow has no items yet but we're rendering, show nothing.
    return kinds.size ? Array.from(kinds) : [];
  })();

  // For each applicable kind, decide if buyer needs to (re-)accept its terms.
  const userVersions = user?.terms_versions_agreed || {};
  const legacyAgreed = user?.terms_version_agreed ?? null;
  const applicable = cartKinds
    .map((k) => {
      const t = termsByKind?.[k];
      if (!t || !t.text) return null;
      const agreedV = userVersions[k] ?? legacyAgreed;
      const needsAccept = agreedV !== t.version;
      return { kind: k, ...t, needsAccept };
    })
    .filter(Boolean);
  const needsTermsAccept = applicable.some((t) => t.needsAccept);

  // Build the combined markdown shown to the buyer (only kinds that apply).
  const combinedTermsMd = applicable
    .map((t) => {
      const heading = t.kind === "prebook" ? "Prebook orders" : "In-Stock orders";
      return `## ${heading}\n\n${t.text}`;
    })
    .join("\n\n---\n\n");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (belowMinOrder) {
      setErr(`Minimum order is ${minOrderUnits} units. Add ${unitsToMin} more before submitting.`);
      return;
    }
    if (!address.trim()) {
      setErr("Please provide a shipping address.");
      return;
    }
    const parts = address.split(",").map((p) => p.trim());
    if (!isAddressComplete({
      line1: parts[0],
      city: parts.length >= 2 ? parts[parts.length - 2] : "",
      postal_code: parts.length >= 2 ? parts[parts.length - 1] : "",
    })) {
      setErr("Please enter street, city, and a valid Canadian postal code (A1A 1A1).");
      return;
    }
    if (needsTermsAccept && !termsAccepted) {
      setErr("Please review and accept the wholesale terms to continue.");
      return;
    }
    setBusy(true);
    try {
      // Record the user as accepting the current version of every applicable kind.
      const acceptedByKind = applicable.reduce((acc, t) => {
        acc[t.kind] = t.version;
        return acc;
      }, {});
      const orders = await createOrder({
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
        shipping_address: address.trim(),
        notes: notes.trim(),
        terms_accepted_versions: acceptedByKind,
      });
      const list = Array.isArray(orders) ? orders : [orders];
      setPlacedOrders(list);
      setSubmitted(true);
      clearCart();
      toast.success(
        list.length > 1
          ? `Order split into ${list.length} invoices · emails sent`
          : "Order submitted · invoice generated"
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Could not submit order. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // ----- Success / confirmation view -----
  if (submitted && placedOrders.length > 0) {
    return (
      <SuccessView
        orders={placedOrders}
        settings={settings}
        shippingAddress={address}
      />
    );
  }

  // ----- Checkout form -----
  return (
    <div className="page-card" data-testid={testIds.checkoutPage}>
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Wholesale checkout</p>
        <h1 className="text-2xl md:text-3xl font-semibold mt-0.5 mb-8">Submit your order</h1>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          <form onSubmit={submit} className="md:col-span-3 space-y-5">
            <fieldset className="space-y-4 border border-border rounded-md p-5">
              <legend className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground px-1">Buyer</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <Info label="Business" value={user.business_name || "—"} />
                <Info label="Contact" value={user.contact_name || "—"} />
                <Info label="Email" value={user.email} />
                <Info label="Phone" value={user.phone || "—"} />
                <Info label="Province" value={user.province || "—"} />
              </div>
            </fieldset>

            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground block mb-1.5">Billing &amp; shipping address</label>
              <AddressFields
                value={address}
                onChange={setAddress}
                testIdPrefix="checkout-address"
                showLabels={true}
              />
              <input
                type="hidden"
                data-testid={testIds.checkoutAddress}
                value={address}
                readOnly
              />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground block mb-1.5">Order notes (optional)</label>
              <textarea
                data-testid={testIds.checkoutNotes}
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Delivery instructions, PO reference, etc."
                className="w-full px-3 py-2.5 text-sm border border-border rounded focus:border-primary"
              />
            </div>


            {needsTermsAccept && (
              <div
                data-testid={testIds.checkoutTermsBanner}
                className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-4 space-y-3"
              >
                <div className="flex items-start gap-2.5">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" strokeWidth={1.75} />
                  <p className="text-sm font-medium">
                    Please review and accept our updated terms below before submitting your order.
                  </p>
                </div>
                <div
                  data-testid={testIds.checkoutTermsText}
                  className="prose-terms max-h-72 overflow-auto rounded border border-amber-200 bg-white/70 p-3 text-sm text-foreground"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(combinedTermsMd) }}
                />
                <label className="flex items-start gap-2.5 text-sm cursor-pointer">
                  <input
                    data-testid={testIds.checkoutTermsCheckbox}
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-amber-600"
                  />
                  <span>
                    I have read and accept the wholesale terms
                    {" "}({applicable.map((t) => `${t.kind === "prebook" ? "Prebook" : "In-Stock"} v${t.version}`).join(" · ")}).
                  </span>
                </label>
              </div>
            )}

            {err && <p className="text-sm text-destructive" data-testid={testIds.checkoutError}>{err}</p>}

            <button
              type="submit"
              data-testid={testIds.checkoutSubmit}
              disabled={busy}
              className="w-full py-3 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Submitting…" : "Place wholesale order"}
            </button>
          </form>

          <aside className="md:col-span-2">
            <div className="border border-border rounded-md sticky top-[calc(var(--topbar-h)+16px)]">
              <div className="px-4 py-3 bg-secondary border-b border-border">
                <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Order summary</p>
              </div>
              <ul className="divide-y divide-border text-sm">
                {items.map((i) => (
                  <li key={`${i.product_id}::${i.size}`} className="px-4 py-3 flex justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{i.name}</p>
                      <p className="text-[11px] text-muted-foreground mb-1.5">
                        {i.size ? `Size ${i.size}` : ""}
                      </p>
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center border border-border rounded">
                          <button
                            type="button"
                            onClick={() => updateQty(i.product_id, i.size, Math.max(0, i.quantity - 1))}
                            className="px-1.5 py-1 hover:bg-secondary"
                            aria-label="Decrease quantity"
                          >
                            <Minus size={12} strokeWidth={1.75} />
                          </button>
                          <input
                            type="number"
                            min="0"
                            value={i.quantity}
                            onChange={(e) => {
                              const n = parseInt(e.target.value, 10);
                              updateQty(i.product_id, i.size, Number.isFinite(n) ? Math.max(0, n) : 0);
                            }}
                            className="w-10 text-center text-xs bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                          <button
                            type="button"
                            onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)}
                            className="px-1.5 py-1 hover:bg-secondary"
                            aria-label="Increase quantity"
                          >
                            <Plus size={12} strokeWidth={1.75} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(i.product_id, i.size)}
                          className="p-1 text-muted-foreground hover:text-destructive"
                          aria-label="Remove item"
                        >
                          <Trash2 size={13} strokeWidth={1.6} />
                        </button>
                      </div>
                    </div>
                    <p className="tabular-nums shrink-0">{fmtCAD((i.wholesale_price || 0) * i.quantity)}</p>
                  </li>
                ))}
              </ul>
              <div className="px-4 py-3 border-t border-border space-y-1">
                <Row label="Units" value={String(totalUnits)} mono />
                <Row label="Subtotal" value={fmtCAD(subtotal)} mono bold />
              </div>
              <div className="px-4 pb-3 text-[11px] text-muted-foreground space-y-2 border-t border-border pt-3">
                <p>
                  <span className="font-medium uppercase tracking-[0.12em] text-[10px] text-foreground block mb-0.5">Shipping</span>
                  Orders ship from North Vancouver, BC via Canada Post or courier. Shipping is invoiced at cost and will be included on your order invoice.
                </p>
                <p>
                  <span className="font-medium uppercase tracking-[0.12em] text-[10px] text-foreground block mb-0.5">Tax</span>
                  GST 5% applies to all orders. PST 6% applies if your province is SK. Final tax shown on invoice.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ============== Success / Invoice confirmation view ==============
function SuccessView({ orders, settings, shippingAddress }) {
  const isSplit = orders.length > 1;
  return (
    <div className="page-card" data-testid={testIds.checkoutSuccess}>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex items-start gap-3 mb-6">
          <CheckCircle2 size={28} strokeWidth={1.5} className="text-emerald-600 shrink-0" />
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Order confirmed</p>
            <h1 className="text-2xl md:text-3xl font-semibold mt-0.5">Thank you — your order is in.</h1>
            <p className="text-sm text-muted-foreground mt-1">
              A confirmation email is on its way. An admin will review and issue the invoice shortly.
            </p>
          </div>
        </div>

        {isSplit && (
          <div
            data-testid={testIds.checkoutSplitNotice}
            className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-4 mb-6 flex items-start gap-2.5"
          >
            <InfoIcon size={16} className="mt-0.5 shrink-0" strokeWidth={1.75} />
            <div className="text-sm">
              <p className="font-medium mb-0.5">Your cart was split into {orders.length} separate orders.</p>
              <p>
                Prebook and In-Stock items ship on different timelines and have different payment terms, so they are
                invoiced separately:&nbsp;
                <span className="font-medium">
                  {orders.map((o) => o.order_number || o.id).join(" · ")}
                </span>
              </p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {orders.map((order) => (
            <OrderReceipt key={order.id} order={order} shippingAddress={shippingAddress} />
          ))}
        </div>

        {(() => {
          const hasPrebook = orders.some((o) => (o.fulfillment_type || "prebook") === "prebook");
          const hasInStock = orders.some((o) => o.fulfillment_type === "in_stock");
          const windowByKind = orders
            .map((o) => o.delivery_window || (o.items || []).find((i) => i.delivery_window)?.delivery_window)
            .filter(Boolean)[0];
          return (
            <div className="mt-8 rounded-md border border-border bg-secondary/40 p-5 md:p-6" data-testid="checkout-success-next">
              <p className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground mb-3">What happens next</p>
              <ol className="space-y-2.5 text-sm">
                <li className="flex gap-3">
                  <span className="text-primary font-semibold">1.</span>
                  <span><strong>Order confirmation email</strong> — you'll get it within the next few minutes. Reply if anything looks off.</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-primary font-semibold">2.</span>
                  <span><strong>We confirm your order</strong> — within 1–2 business days we'll email you an <em>Order Confirmed</em> notice with your ship window and the full wholesale terms.</span>
                </li>
                {hasPrebook && (
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold">3.</span>
                    <span>
                      <strong>Ship window{windowByKind ? `: ${windowByKind}` : ""}</strong> — prebook items ship within the drop's window. You'll receive tracking as soon as it goes out.
                    </span>
                  </li>
                )}
                {hasInStock && (
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold">{hasPrebook ? "4" : "3"}.</span>
                    <span><strong>In-stock ships within a few business days</strong> — items are being packed now. You'll receive tracking as soon as they leave the warehouse.</span>
                  </li>
                )}
                <li className="flex gap-3">
                  <span className="text-primary font-semibold">{(hasPrebook ? 1 : 0) + (hasInStock ? 1 : 0) + 3}.</span>
                  <span><strong>Invoice on shipment</strong> — the final invoice (including shipping calculated at pack-out) is emailed when your order ships. Payment is {hasPrebook && !hasInStock ? "Net 15 from invoice date" : hasInStock && !hasPrebook ? "due before shipment" : "Net 15 for prebook, due before shipment for in-stock"}.</span>
                </li>
              </ol>
            </div>
          );
        })()}

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/orders"
            data-testid={testIds.checkoutSuccessViewOrders}
            className="inline-flex items-center px-4 py-2.5 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
          >
            View my orders
          </Link>
          <Link
            to="/shop"
            className="inline-flex items-center px-4 py-2.5 rounded border border-border text-sm font-medium hover:bg-secondary"
          >
            Continue browsing
          </Link>
        </div>
      </div>
    </div>
  );
}

function OrderReceipt({ order, shippingAddress }) {
  const orderRef = order.order_number || order.id;
  const fulfillmentLabel = FULFILLMENT_LABEL[order.fulfillment_type] || order.fulfillment_type;
  const isSK = (order.province || "").toUpperCase() === "SK";
  return (
    <section
      data-testid={testIds.checkoutSuccessOrder}
      data-order-ref={orderRef}
      className="border border-border rounded-md overflow-hidden"
    >
      <header className="px-5 py-4 bg-secondary border-b border-border flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Order</p>
          <p className="text-base font-semibold tabular-nums">{orderRef}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{fulfillmentLabel}</p>
          <p className="text-sm font-medium">{order.payment_terms || "—"}</p>
          {order.due_date && (
            <p className="text-[11px] text-muted-foreground mt-0.5">Due {order.due_date}</p>
          )}
        </div>
      </header>

      <ul className="divide-y divide-border text-sm">
        {(order.items || []).map((li, idx) => (
          <li key={`${li.product_id}-${li.size || ""}-${idx}`} className="px-5 py-3 flex justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate">{li.name}</p>
              <p className="text-[11px] text-muted-foreground">
                {li.sku ? `SKU ${li.sku} · ` : ""}{li.size ? `Size ${li.size} · ` : ""}Qty {li.quantity}
              </p>
            </div>
            <p className="tabular-nums shrink-0">{fmtCAD(li.line_total)}</p>
          </li>
        ))}
      </ul>

      <div className="px-5 py-4 border-t border-border space-y-1">
        <Row label="Units" value={String((order.items || []).reduce((s, li) => s + Number(li.quantity || 0), 0))} mono />
        <Row label="Subtotal" value={fmtCAD(order.subtotal)} mono />
        <Row label="GST (5%)" value={fmtCAD(order.gst)} mono />
        {isSK && <Row label="PST (6%) — SK" value={fmtCAD(order.pst)} mono />}
        <div className="border-t border-border pt-1.5 mt-1.5">
          <Row label="Total" value={fmtCAD(order.total)} mono bold />
        </div>
      </div>

      {shippingAddress && (
        <div className="px-5 py-3 border-t border-border bg-secondary/60">
          <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-1">Shipping to</p>
          <p className="text-sm whitespace-pre-wrap">{shippingAddress}</p>
        </div>
      )}
    </section>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
      <p className="truncate">{value}</p>
    </div>
  );
}

function Row({ label, value, mono, bold }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${mono ? "tabular-nums" : ""} ${bold ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}
